import {useEffect, useState} from 'react';

import {useSocket} from './index';

/**
 * Tracks the live connected/disconnected state of the shared game socket.
 *
 * PlayChess derives the same flag inline by attaching connect/disconnect/close
 * listeners (see PlayChess/index.js ~L1462). Both PlayChess and Fun Challenges
 * share the SAME socket instance from SocketProvider, so this hook centralises
 * that logic for reuse — notably so Fun Challenges can show a "Lost connection"
 * indicator when another client (e.g. the app) takes over the socket, which
 * surfaces as a socket-level disconnect/close rather than only as the
 * `change_client_connection` message.
 */
export const useSocketConnected = (): boolean => {
  const socket = useSocket();
  const [connected, setConnected] = useState<boolean>(!!socket?.connected);

  useEffect(() => {
    if (!socket || typeof socket.on !== 'function') {
      setConnected(false);
      return;
    }
    const up = () => setConnected(true);
    const down = () => setConnected(false);

    setConnected(!!socket.connected);

    socket.on('connect', up);
    socket.on('disconnect', down);
    socket.on('close', down);

    return () => {
      try {
        socket.off?.('connect', up);
        socket.off?.('disconnect', down);
        socket.off?.('close', down);
      } catch {
        // ignore cleanup failures
      }
    };
  }, [socket]);

  return connected;
};

export default useSocketConnected;
